<script lang="ts">
	import type Dropzone from "dropzone"

	import { DataHandlerFolderAdmin, DataHandlerMediaAdmin, Media } from "luxedo-data"
	import { FileSystemMedia } from "../../../modules/FileSystems"

	import type { Folder } from "luxedo-data/src/entries/Folder"
	import { splitFileName } from "../../../modules/FileUtil"

	import { Switch } from "misc-ui"
	import { Toast } from "toaster"
	import DropzoneInput from "../../reusable/input/DropzoneInput.svelte"
	import { LuxedoRPC } from "luxedo-rpc"
	import { onMount } from "svelte"

	interface UploadFile extends Dropzone.DropzoneFile {
		hash: string
	}

	const uploadUrl = `${import.meta.env.VITE_API_URL}/workspace/media/splitupload`

	interface Props {
		activeFolder: Folder
		postSubmit: () => void
	}

	let { activeFolder, postSubmit }: Props = $props()

	let processingIndex: number = 0
	let pendingFiles: Array<UploadFile> = []
	let uploadedFiles: Array<UploadFile> = []
	let completedFiles: Array<Media> = []

	let uploadType: "media" | "folder" = $state("media")
	let newFileName: string = $state()

	let fileIdHashMap: {
		[index: string]: number
	} = {}

	/**
	 * Called when folder form is submitted
	 * @param e
	 */
	async function onSubmit(e: Event) {
		e.preventDefault()
		// Upload is handled by dropzone if upload type is media
		if (uploadType === "media") return

		await onCreateFolder()
		postSubmit()
	}

	/**
	 * Creates a folder on the server and updates the FileSystem
	 */
	async function onCreateFolder() {
		await DataHandlerFolderAdmin.createEntry({
			name: newFileName,
			parent_id: activeFolder.id,
		})
		await FileSystemMedia.refreshData()
	}

	/**
	 * Resets state to avoid odd behaviors
	 */
	function resetState() {
		fileIdHashMap = {}
		pendingFiles = []
		uploadedFiles = []
		completedFiles = []
		processingIndex = 0
	}

	/**
	 * Called when dropzone form is submitted
	 */
	async function onUpload(files: Array<UploadFile>, name: string, dz: Dropzone) {
		resetState()
		for (const file of files) {
			await createFileRecord(file)
			pendingFiles.push(file)
		}
		dz.processQueue()
	}

	/**
	 * Called by Dropzone when the file data is being sent to the server. Called AFTER createFileRecord
	 */
	async function onDropzoneSend(file: UploadFile, xhr: XMLHttpRequest, formData: FormData) {
		formData.append("file_id", String(fileIdHashMap[file.hash]))
	}

	/**
	 * Creates a DB record of the new media file before submitting the actual file
	 * @param uploadFile
	 */
	async function createFileRecord(uploadFile: UploadFile) {
		const [name] = splitFileName(uploadFile.name)

		const entryID = await DataHandlerMediaAdmin.createEntry({
			name,
			parent_id: activeFolder.id,
			storage_mimetype: uploadFile.type,
		})
		const fileHash = DataHandlerMediaAdmin.get(entryID).hash()

		uploadFile.hash = fileHash
		fileIdHashMap[fileHash] = entryID
	}

	/**
	 * Delete all DB entries that have been uploaded during this session. (Used in case of upload/processing failure)
	 */
	async function deleteAttemptedUploads() {
		for (const file of uploadedFiles) {
			const fileID = fileIdHashMap[file.hash]
			const entry = DataHandlerMediaAdmin.get(fileID)
			await DataHandlerMediaAdmin.deleteEntry(entry)
		}
	}

	/**
	 * Called by Drozone in case the upload fails
	 */
	function onFail(file: UploadFile, message: string | Error, xhr: XMLHttpRequest) {
		Toast.error("Error uploading files.")
		deleteAttemptedUploads()
	}

	/**
	 * Called by Dropzone when the upload is succesful
	 */
	function onSuccess(file: UploadFile) {
		uploadedFiles.push(file)
		if (uploadedFiles.length === pendingFiles.length) {
			processNextFile()
		}
	}

	/**
	 * After db entries are created and the file itself has been uploaded to the server, process each file to generate thumbnails, ect.
	 */
	async function processNextFile() {
		if (processingIndex === uploadedFiles.length) {
			await DataHandlerMediaAdmin.pull(completedFiles.map((media) => media.id))
			Toast.success("All files successfully processed")
			postSubmit()
			return
		}

		const fileID = fileIdHashMap[uploadedFiles[processingIndex].hash]
		const fileEntry = DataHandlerMediaAdmin.get(fileID)
		const mimetype = fileEntry.mimetype

		try {
			await LuxedoRPC.api.admin.media_process_admin(fileID, mimetype)
			processingIndex++
			completedFiles.push(fileEntry)
			processNextFile()
		} catch (error) {
			deleteAttemptedUploads()
			Toast.error("Unable to process file.")
			console.error("Unable to process media file.", error)
			// Reset state to clean up any partial state
			resetState()
		}
	}

	onMount(() => {
		console.log({ activeFolder, postSubmit })
	})
</script>

<form class="overlay-form" onsubmit={onSubmit}>
	<div id="switch-container">
		<Switch
			id="create-new-option"
			bind:value={uploadType}
			options={{ Media: "media", Folder: "folder" }}
			small
		/>
	</div>

	{#if uploadType === "media"}
		<DropzoneInput uploadMultiple {uploadUrl} {onUpload} {onDropzoneSend} {onFail} {onSuccess} />
	{:else}
		<div class="input-container">
			<label for="folder-name-input"
				>{`${uploadType.charAt(0).toUpperCase()}${uploadType.slice(1)}`} Name</label
			>
			<input id="folder-name-input" bind:value={newFileName} />
		</div>
		<button>Create</button>
	{/if}
</form>

<style>
	#switch-container {
		position: absolute;
		display: flex;
		flex-direction: row;
		top: 0.5rem;
		right: 0.75rem;
		z-index: 15;
	}

	#switch-container :global(.Switch) {
		margin-right: 2rem;
	}

	.overlay-form :global(.file-uploader) {
		width: 100%;
		height: 8rem;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		border-radius: var(--br);
		background-color: var(--color-bg);
		color: var(--color-text);
		padding: 1rem;
	}
</style>
